// Whether a stream actually has a picture right now. A video track being present isn't enough: a received track
// goes `muted` when the sender says it stopped, but a sender that just stops sending frames (camera switched
// off, unplugged, connection stalled) leaves it looking live, frozen on its last frame. So when the <video>
// showing the stream is passed in, frames are watched too: none for STALL_MS counts as no picture until they
// resume. (Needs requestVideoFrameCallback, and doesn't fire while the page isn't being painted - the video
// has to stay displayed, not display:none, for this to see it.) Skip `video` for a source that legitimately
// sends few frames, like a screen share of a static window.
//
// Calls onChange(hasPicture) straight away and on every later change; returns a function that stops watching.
const STALL_MS = 2000;
const STALL_CHECK_MS = 500;

export function watchPicture(stream, onChange, { video = null } = {}) {
	const tracks = new Set();
	let last;
	let stalled = false;
	let lastFrameAt = Date.now();
	let stopped = false;
	let frameCallback = null;
	let stallTimer = null;

	const check = () => {
		const has = !!stream && !stalled && stream.getVideoTracks().some(t => t.readyState === 'live' && !t.muted);
		if(has !== last) { last = has; onChange(has); }
	};
	const listen = track => {
		if(tracks.has(track)) return;
		tracks.add(track);
		for(const event of [ 'mute', 'unmute', 'ended' ]) track.addEventListener(event, check);
	};
	const scan = () => { for(const t of stream?.getVideoTracks() || []) listen(t); check(); };

	if(video?.requestVideoFrameCallback && stream) {
		const onFrame = () => {
			if(stopped) return;
			lastFrameAt = Date.now();
			if(stalled) { stalled = false; check(); }
			frameCallback = video.requestVideoFrameCallback(onFrame);
		};
		frameCallback = video.requestVideoFrameCallback(onFrame);
		stallTimer = setInterval(() => {
			if(!stalled && Date.now() - lastFrameAt > STALL_MS) { stalled = true; check(); }
		}, STALL_CHECK_MS);
	}

	stream?.addEventListener('addtrack', scan);
	stream?.addEventListener('removetrack', scan);
	scan();
	return () => {
		stopped = true;
		clearInterval(stallTimer);
		if(frameCallback !== null) video?.cancelVideoFrameCallback?.(frameCallback);
		stream?.removeEventListener('addtrack', scan);
		stream?.removeEventListener('removetrack', scan);
		for(const t of tracks) for(const event of [ 'mute', 'unmute', 'ended' ]) t.removeEventListener(event, check);
		tracks.clear();
	};
}
